import AutorenewIcon from "@mui/icons-material/Autorenew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SendIcon from "@mui/icons-material/Send";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import { Box, Typography, Button, IconButton } from "@mui/material";
import React from "react";

import { MediaItem } from "./PreviewPane";

// Globe icon used in Facebook and LinkedIn previews
const Globe: React.FC = () => (
  <Box component="svg" viewBox="0 0 16 16" fill="currentColor" sx={{ width: 16, height: 16 }}>
    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm5.91 7h-2.03c-.08-1.37-.39-2.67-.88-3.81 1.68.83 2.91 2.31 2.91 3.81zM8 2c.55 0 1.51 1.21 1.91 3H6.09c.4-1.79 1.36-3 1.91-3zm-5.91 5h2.03c.08 1.37.39 2.67.88 3.81-1.68-.83-2.91-2.31-2.91-3.81zM8 14c-.55 0-1.51-1.21-1.91-3h3.82c-.4 1.79-1.36 3-1.91 3zm2.15-4H5.85c-.09-1.32-.09-2.68 0-4h4.3c.09 1.32.09 2.68 0 4z" />
  </Box>
);

interface LinkedInPreviewProps {
  content: string;
  media: MediaItem[];
}

export const LinkedInPreview: React.FC<LinkedInPreviewProps> = ({ content, media }) => {
  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", overflow: "hidden" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "grey.300",
              flexShrink: 0,
            }}
          />
          <Box sx={{ ml: 2, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Il tuo account
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "grey.500",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              Company • Following
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "grey.500",
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              Just now
              <Globe />
            </Typography>
          </Box>
        </Box>
        <IconButton sx={{ p: 1, "&:hover": { bgcolor: "grey.100" }, flexShrink: 0 }}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            overflowWrap: "break-word",
            wordWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {content || (
            <span style={{ color: "grey" }}>
              Il tuo post apparirà qui. Scrivi qualcosa di interessante!
            </span>
          )}
        </Typography>
        {media.length > 0 && (
          <Box
            sx={{
              mb: 2,
              display: "grid",
              gridTemplateColumns: media.length === 1 ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            {media.map((item) => (
              <Box
                key={item.id}
                component="img"
                src={item.url}
                alt=""
                sx={{
                  width: "100%",
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                  maxHeight: 400,
                }}
              />
            ))}
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2, mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              color: "grey.500",
            }}
          >
            <ThumbUpAltOutlinedIcon fontSize="small" sx={{ color: "blue", mr: 0.5 }} />
            <Typography variant="caption">0 reactions</Typography>
            <Typography variant="caption" sx={{ mx: 1 }}>
              •
            </Typography>
            <Typography variant="caption">0 comments</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Button
              sx={{
                textTransform: "none",
                px: { xs: 1, sm: 2 },
                py: 0.5,
                borderRadius: 2,
                color: "grey.600",
                "&:hover": { bgcolor: "grey.100" },
                minWidth: "auto",
                flexGrow: { xs: 1, sm: 0 },
                flexBasis: { xs: "45%", sm: "auto" },
              }}
              startIcon={<ThumbUpAltOutlinedIcon fontSize="small" />}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  display: { xs: "none", sm: "block" },
                }}
              >
                Like
              </Typography>
            </Button>
            <Button
              sx={{
                textTransform: "none",
                px: { xs: 1, sm: 2 },
                py: 0.5,
                borderRadius: 2,
                color: "grey.600",
                "&:hover": { bgcolor: "grey.100" },
                minWidth: "auto",
                flexGrow: { xs: 1, sm: 0 },
                flexBasis: { xs: "45%", sm: "auto" },
              }}
              startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  display: { xs: "none", sm: "block" },
                }}
              >
                Comment
              </Typography>
            </Button>
            <Button
              sx={{
                textTransform: "none",
                px: { xs: 1, sm: 2 },
                py: 0.5,
                borderRadius: 2,
                color: "grey.600",
                "&:hover": { bgcolor: "grey.100" },
                minWidth: "auto",
                flexGrow: { xs: 1, sm: 0 },
                flexBasis: { xs: "45%", sm: "auto" },
              }}
              startIcon={<AutorenewIcon fontSize="small" />}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  display: { xs: "none", sm: "block" },
                }}
              >
                Repost
              </Typography>
            </Button>
            <Button
              sx={{
                textTransform: "none",
                px: { xs: 1, sm: 2 },
                py: 0.5,
                borderRadius: 2,
                color: "grey.600",
                "&:hover": { bgcolor: "grey.100" },
                minWidth: "auto",
                flexGrow: { xs: 1, sm: 0 },
                flexBasis: { xs: "45%", sm: "auto" },
              }}
              startIcon={<SendIcon fontSize="small" />}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  display: { xs: "none", sm: "block" },
                }}
              >
                Send
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
